import type { TaggedImage } from '@watchkeeper/shared-types';

import type { ScrapedWatchEntry } from './BulkImport';
import { squareProductDocId } from './storeDocIds';
import { storeDescriptionText } from './storeText';

export { squareProductDocId };

/**
 * Square Online / Weebly storefront import — the sibling of ShopifyScraper.
 *
 * Every Square Online storefront, on a `*.square.site` subdomain or a custom
 * domain, is server-rendered with one `window.__BOOTSTRAP_STATE__ = {…}` blob
 * carrying the two ids its public catalogue API is addressed by:
 *
 *   userId  — siteData.user.id
 *   siteId  — siteData.site.properties.catalogSiteId
 *
 * plus `commerceLinks.categories`, every category's numeric id, name and link.
 * The catalogue itself is a public, unauthenticated JSON API on
 * `cdn5.editmysite.com` that answers with `Access-Control-Allow-Origin: *`, so
 * the browser pages through it directly. The storefront HTML does NOT send CORS
 * headers, so that one page is fetched through the existing `fetchHtml`
 * callable — see `resolveSquareCategory` callers.
 *
 * Scope: a pasted CATEGORY URL imports that category; any other storefront
 * URL (home page, `/s/shop`) imports the WHOLE shop — the common case, a
 * single-brand shop. Either way everything lands under one brand, one
 * reference per product (multi-SKU products are flagged, not split into
 * variants), and only products the merchant has made visible — the API will
 * also return merchant-hidden products on request, and those are not ours to
 * import.
 *
 * Recon notes: scratchpad/squareonline/MEMO.md.
 */

export const SQUARE_API_BASE = 'https://cdn5.editmysite.com/app/store/api/v28/editor';
/** The largest page the storefront itself asks for. */
export const SQUARE_PAGE_SIZE = 60;
/** Runaway guard: 100 pages × 60 is far past any storefront we have seen. */
const MAX_PAGES = 100;

export interface SquareCategory {
  /** The numeric id in the storefront URL, e.g. "41". */
  siteCategoryId: string;
  name: string;
  /** Storefront-relative link, e.g. "/shop/dave-berghold-watches/41". */
  siteLink: string;
}

export interface SquareStore {
  userId: string;
  siteId: string;
  /** The public host, custom domain if set, else the square.site subdomain. */
  domain?: string;
  categories: SquareCategory[];
}

/**
 * What a pasted URL asks for: one category, or the whole shop. A product page
 * is neither (it is refused rather than read as "everything").
 */
export type ResolvedSquareScope =
  | { store: SquareStore; scope: 'category'; category: SquareCategory }
  | { store: SquareStore; scope: 'shop'; category?: undefined };

const BOOTSTRAP_MARKER = '__BOOTSTRAP_STATE__';

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v && typeof v === 'object' ? (v as Rec) : {});
const idString = (v: unknown): string | undefined =>
  (typeof v === 'string' && v.trim()) || (typeof v === 'number' && Number.isFinite(v))
    ? String(v).trim()
    : undefined;

/**
 * Reads one JSON value starting at `start`, tracking string escapes so a `}`
 * inside a product name cannot end the object early. Returns the text of the
 * value, or null if it never closes.
 */
function sliceJsonObject(text: string, start: number): string | null {
  if (text[start] !== '{') return null;
  let depth = 0;
  let inString = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') i++;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') depth++;
    else if (ch === '}' && --depth === 0) return text.slice(start, i + 1);
  }
  return null;
}

/**
 * The store ids and category list from a storefront page's HTML, or null when
 * the page is not a Square Online storefront (no bootstrap blob, or one without
 * both ids — a password-protected or "coming soon" site renders neither).
 */
export function parseSquareBootstrap(html: string): SquareStore | null {
  const at = html.indexOf(BOOTSTRAP_MARKER);
  if (at === -1) return null;
  const open = html.indexOf('{', at);
  if (open === -1) return null;
  const raw = sliceJsonObject(html, open);
  if (!raw) return null;

  let state: Rec;
  try {
    state = rec(JSON.parse(raw));
  } catch {
    return null;
  }

  const siteData = rec(state.siteData);
  const props = rec(rec(siteData.site).properties);
  const domain = rec(props.domain);
  const userId = idString(rec(siteData.user).id);
  const siteId = idString(props.catalogSiteId) ?? idString(domain.site_id);
  if (!userId || !siteId) return null;

  const categories: SquareCategory[] = Object.values(
    rec(rec(state.commerceLinks).categories),
  )
    .map((c) => {
      const r = rec(c);
      const siteCategoryId = idString(r.site_category_id);
      const name = typeof r.name === 'string' ? r.name.trim() : '';
      if (!siteCategoryId || !name) return null;
      return {
        siteCategoryId,
        name,
        siteLink: typeof r.site_link === 'string' ? r.site_link : '',
      };
    })
    .filter((c): c is SquareCategory => c !== null);

  const host =
    (typeof domain.custom_domain === 'string' && domain.custom_domain.trim()) ||
    (typeof domain.subdomain === 'string' && domain.subdomain.trim()) ||
    undefined;

  return { userId, siteId, domain: host, categories };
}

/**
 * The numeric category id a storefront URL points at:
 *   …/shop/{slug}/{id}       (both square.site and custom domains)
 *   …/s/shop?category={id}   (the store root with a category filter)
 * Null for anything else, including the bare store root.
 */
export function categoryIdFromUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  const fromQuery = parsed.searchParams.get('category')?.trim();
  if (fromQuery && /^\d+$/.test(fromQuery)) return fromQuery;
  const match = parsed.pathname.match(/\/shop\/[^/]+\/(\d+)\/?$/);
  return match ? match[1] : null;
}

/**
 * Decide what a pasted URL imports, given the store parsed from that page.
 *
 *  - A category link (`…/shop/{slug}/{id}`, `?category={id}`) → that category.
 *    The id must be one of the store's own categories, so a stale link fails
 *    here rather than importing nothing — or, worse, everything.
 *  - A product page (`…/product/…`) → refused: almost certainly a mis-paste,
 *    and reading it as "the whole shop" would surprise.
 *  - Anything else on the storefront (home page, `/s/shop`) → the whole shop.
 */
export function resolveSquareScope(
  url: string,
  store: SquareStore,
): ResolvedSquareScope | { error: string } {
  const id = categoryIdFromUrl(url);
  if (id) {
    const category = store.categories.find((c) => c.siteCategoryId === id);
    if (!category) {
      return { error: `Category ${id} is not one of this store's categories.` };
    }
    return { store, scope: 'category', category };
  }
  let path: string;
  try {
    path = new URL(url.trim()).pathname;
  } catch {
    return { error: 'That is not a valid URL.' };
  }
  if (/\/product\//.test(path)) {
    return {
      error:
        'That is a product page. Paste the shop’s home page to import everything, or a category link (…/shop/<name>/<number>) for one category.',
    };
  }
  return { store, scope: 'shop' };
}

// ── Catalogue API ───────────────────────────────────────────────────────────

export interface SquareImage {
  absolute_url?: string;
  url?: string;
  urls?: Record<string, string>;
}

export interface SquareSku {
  id?: string;
  sku?: string | null;
  name?: string | null;
  price?: { current?: number } | null;
  option_choices?: { data?: Array<{ name?: string; choice?: string }> } | null;
}

export interface SquareProduct {
  id: string;
  name: string;
  short_description?: string | null;
  absolute_site_link?: string;
  visibility?: string;
  badges?: { out_of_stock?: boolean };
  price?: { low?: number; high?: number; regular_low?: number };
  images?: { data?: SquareImage[] };
  skus?: { data?: SquareSku[] };
  /**
   * The categories it is filed in. Mixes Square's internal (hex) ids with the
   * storefront's numeric site category ids — only the latter match the store's
   * category list, so only those are read.
   */
  categoryIds?: Array<string | number>;
}

interface SquarePage {
  data?: SquareProduct[];
  meta?: { pagination?: { total?: number; total_pages?: number; current_page?: number } };
}

/** The catalogue page URL; without a category id it lists the whole shop. */
export function squareProductsUrl(
  store: Pick<SquareStore, 'userId' | 'siteId'>,
  categoryId: string | undefined,
  page: number,
): string {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(SQUARE_PAGE_SIZE),
    include: 'images,skus',
  });
  if (categoryId) params.append('categories[]', categoryId);
  return `${SQUARE_API_BASE}/users/${encodeURIComponent(store.userId)}/sites/${encodeURIComponent(store.siteId)}/products?${params}`;
}

/**
 * Every visible product in one category — or the whole shop when `categoryId`
 * is undefined — all pages. The default listing is `visible` only, which is
 * what we want; nothing here asks for hidden ones. Products are deduped by id
 * (a product can sit in a category and its parent).
 */
export async function fetchSquareProducts(
  store: Pick<SquareStore, 'userId' | 'siteId'>,
  categoryId: string | undefined,
  options: {
    fetchImpl?: typeof fetch;
    onPage?: (page: number, totalPages: number) => void;
  } = {},
): Promise<SquareProduct[]> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const byId = new Map<string, SquareProduct>();
  for (let page = 1; page <= MAX_PAGES; page++) {
    const resp = await fetchImpl(squareProductsUrl(store, categoryId, page));
    if (!resp.ok) {
      throw new Error(`Square catalogue returned HTTP ${resp.status} on page ${page}`);
    }
    const body = (await resp.json()) as SquarePage;
    const rows = body.data ?? [];
    for (const p of rows) {
      if (p?.id && p.visibility !== 'hidden') byId.set(p.id, p);
    }
    const totalPages = body.meta?.pagination?.total_pages ?? page;
    options.onPage?.(page, totalPages);
    if (rows.length === 0 || page >= totalPages) break;
  }
  return [...byId.values()];
}

// ── Mapping to the Bulk Import entry ───────────────────────────────────────

/** The largest rendition the CDN offers, else the original. */
export function squareImageUrl(img: SquareImage): string | undefined {
  const urls = img.urls ?? {};
  const widest = Object.keys(urls)
    .filter((k) => /^\d+$/.test(k))
    .sort((a, b) => Number(b) - Number(a))[0];
  const url = (widest && urls[widest]) || img.absolute_url || img.url;
  return url?.trim() || undefined;
}

/** Description HTML → text; shared with the other store scrapers. */
export const squareDescriptionText = storeDescriptionText;

export interface SquareEntry extends ScrapedWatchEntry {
  squareSiteId: string;
  squareProductId: string;
}

/**
 * One Bulk Import entry per product.
 *
 * `reference` is the product's SKU code when it has exactly one distinct code,
 * else the product name — Square stores rarely carry a model number, and the
 * doc id (not this string) is the identity, so a name-as-ref only affects
 * display and search.
 */
export function squareProductToEntry(product: SquareProduct, siteId: string): SquareEntry {
  const name = product.name.trim();
  const codes = [
    ...new Set(
      (product.skus?.data ?? [])
        .map((s) => (s.sku ?? '').trim())
        .filter(Boolean),
    ),
  ];
  const images = (product.images?.data ?? [])
    .map(squareImageUrl)
    .filter((u): u is string => !!u);
  const taggedImages: TaggedImage[] = images.map((url) => ({
    url,
    tags: [],
    autoTagged: false,
  }));
  const price = product.price?.low ?? product.price?.regular_low;
  const optionCount = product.skus?.data?.length ?? 0;

  return {
    reference: codes.length === 1 ? codes[0] : name,
    model: name,
    title: name,
    productName: name,
    url: product.absolute_site_link,
    imgSrc: images[0],
    ...(taggedImages.length > 1 ? { taggedImages } : {}),
    description: squareDescriptionText(product.short_description),
    ...(typeof price === 'number' && price > 0 ? { price } : {}),
    ...(optionCount > 1 ? { storeOptionCount: optionCount } : {}),
    squareSiteId: siteId,
    squareProductId: product.id,
  };
}

/**
 * Products sold with a choice (e.g. case finish). v1 imports each as ONE
 * reference; the count is reported at load so the curator knows which rows
 * stand for several real variants.
 */
export function countMultiOptionProducts(products: SquareProduct[]): number {
  return products.filter((p) => (p.skus?.data?.length ?? 0) > 1).length;
}

export type LoadedSquareSource = ResolvedSquareScope & {
  products: SquareProduct[];
  entries: SquareEntry[];
  /** Products sold with options imported as ONE reference (variant expansion off). */
  multiOptionCount: number;
  /** Multi-option products expanded into one row per SKU (Phase 2). */
  expandedProductCount: number;
};

/**
 * Pasted shop or category URL → store, scope, products and Bulk Import
 * entries. `fetchPageHtml` reads the storefront page server-side (it has no
 * CORS headers); the catalogue pages are fetched directly.
 */
export async function loadSquareSource(
  sourceUrl: string,
  deps: {
    fetchPageHtml: (url: string) => Promise<string>;
    /** Phase 2: one entry per SKU of a multi-option product (default on). */
    expandVariants?: boolean;
    fetchImpl?: typeof fetch;
    onPage?: (page: number, totalPages: number) => void;
  },
): Promise<LoadedSquareSource> {
  const url = sourceUrl.trim();
  if (!/^https:\/\//i.test(url)) throw new Error('Enter the shop or category URL, starting https://');
  const html = await deps.fetchPageHtml(url);
  const store = parseSquareBootstrap(html);
  if (!store) {
    throw new Error(
      'That page is not a Square Online storefront (no store data found). Check the link opens the shop in a browser.',
    );
  }
  const resolved = resolveSquareScope(url, store);
  if ('error' in resolved) throw new Error(resolved.error);
  const products = await fetchSquareProducts(
    store,
    resolved.category?.siteCategoryId,
    { fetchImpl: deps.fetchImpl, onPage: deps.onPage },
  );
  return {
    ...resolved,
    products,
    entries: squareProductsToEntries(
      products,
      store.siteId,
      deps.expandVariants !== false,
      new Map(store.categories.map((c) => [c.siteCategoryId, c.name])),
    ),
    multiOptionCount: deps.expandVariants === false ? countMultiOptionProducts(products) : 0,
    expandedProductCount: deps.expandVariants === false ? 0 : countMultiOptionProducts(products),
  };
}

// ── Phase 2: variants ───────────────────────────────────────────────────────

/** A SKU's own label: its option choices ("Polished", "Brushed / 20mm"), else its name. */
export function squareSkuLabel(sku: SquareSku, index: number): string {
  const choices = (sku.option_choices?.data ?? [])
    .map((c) => (c.choice ?? '').trim())
    .filter(Boolean);
  if (choices.length > 0) return choices.join(' / ');
  return (sku.name ?? '').trim() || `Option ${index + 1}`;
}

/**
 * One Bulk Import entry per SKU of a multi-option product. Square Online
 * keeps images on the product, not the SKU, so every variant shares them; the
 * SKU contributes its code, price and option label. `storeParent` names the
 * parent reference (the product).
 */
export function squareSkuToEntry(
  product: SquareProduct,
  sku: SquareSku,
  index: number,
  siteId: string,
  variantCount: number,
): SquareEntry {
  const base = squareProductToEntry(product, siteId);
  const label = squareSkuLabel(sku, index);
  const code = (sku.sku ?? '').trim();
  const price = sku.price?.current;
  return {
    ...base,
    reference: code || `${base.model} — ${label}`,
    title: `${base.model} — ${label}`,
    variant: label,
    ...(typeof price === 'number' && price > 0 ? { price } : {}),
    squareSkuId: sku.id,
    storeVariantCount: variantCount,
    storeParent: { reference: base.model, imgSrc: base.imgSrc, taggedImages: base.taggedImages },
  };
}

/**
 * Products → entries, expanding multi-option products into one entry per SKU
 * when `expand` is on. Shared by the automatic fetch and the paste fallback.
 */
export function squareProductsToEntries(
  products: SquareProduct[],
  siteId: string,
  expand: boolean,
  /** Site category id → name, from the store's own list. Labels `storeCategories`. */
  categoryNames?: ReadonlyMap<string, string>,
): SquareEntry[] {
  return products.flatMap((p) => {
    const storeCategories = squareStoreCategories(p, categoryNames);
    const withCategories = (e: SquareEntry): SquareEntry =>
      storeCategories.length > 0 ? { ...e, storeCategories } : e;
    const skus = (p.skus?.data ?? []).filter((s) => !!s.id);
    if (!expand || skus.length < 2) return [withCategories(squareProductToEntry(p, siteId))];
    return skus.map((sku, i) => withCategories(squareSkuToEntry(p, sku, i, siteId, skus.length)));
  });
}

/**
 * A product's storefront categories: the numeric site category ids among its
 * `categoryIds` (the hex ones are Square-internal), named from the store's
 * list, or "Category N" when the list does not carry it.
 */
export function squareStoreCategories(
  product: Pick<SquareProduct, 'categoryIds'>,
  categoryNames?: ReadonlyMap<string, string>,
): Array<{ id: string; name: string }> {
  const out: Array<{ id: string; name: string }> = [];
  for (const raw of product.categoryIds ?? []) {
    const id = String(raw).trim();
    if (!/^\d+$/.test(id) || out.some((c) => c.id === id)) continue;
    out.push({ id, name: categoryNames?.get(id) ?? `Category ${id}` });
  }
  return out;
}
